import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { useToaster } from 'hooks/useToaster';
import { ToasterVariant } from 'context/toasterContext';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  updateSlackSettings,
  UpdateSlackSettingsInputType
} from '@event-editor/api/update-slack-settings';

interface DisconnectSlackChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DisconnectSlackChannelModal: React.FC<DisconnectSlackChannelModalProps> = ({
  isOpen,
  onClose
}) => {
  const event = useEditableEvent();
  const queryClient = useQueryClient();
  const { showToast } = useToaster();

  const disconnectMutation = useMutation({
    mutationFn: (variables: UpdateSlackSettingsInputType) => updateSlackSettings(variables),
    onSuccess: (result: any, variables) => {
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                ...result.updateSlackSettings.event
              }
            }
          : oldData
      );
      onClose();
    },
    onError: (error) => {
      console.error('Error disconnecting Slack channel:', error);
      showToast('Failed to disconnect Slack channel. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent size="sm" data-testid="disconnect-slack-modal">
        <ModalHeader className="tw-mb-0">
          <ModalTitle>Are you sure?</ModalTitle>
          <ModalDescription>
            If you disconnect this channel, new guests won’t be added to this channel when they
            register for the event.
          </ModalDescription>
        </ModalHeader>
        <ModalFooter className="tw-mt-5 tw-flex tw-justify-end tw-gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={disconnectMutation.isPending}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (!event.id) return;
              disconnectMutation.mutate({
                eventId: event.id
              });
            }}
            disabled={disconnectMutation.isPending}
            size="sm"
          >
            Disconnect channel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
